/**
 * Helpers for splitting / joining the HTML `datetime-local` wire format
 * ("YYYY-MM-DDTHH:MM") so a single combined field can be presented as a
 * separate date picker + time picker without changing the stored value.
 *
 * The wizard reads/writes `eventDetails.startDateTime` etc. as this exact
 * string (sliced from an ISO string on ICS import, re-expanded to ISO on
 * submit via `new Date(...).toISOString()`). Keeping the round-trip identical
 * means ICS pre-seeding and the submit path are unaffected.
 */

import { DateTime } from 'luxon';

/** Split "YYYY-MM-DDTHH:MM" -> { date: "YYYY-MM-DD", time: "HH:MM" }. */
export function splitDateTimeLocal(value) {
    if (!value || typeof value !== 'string') {
        return { date: '', time: '' };
    }
    const [date = '', timePart = ''] = value.split('T');
    // Guard against seconds/millis ("23:00:00") — keep only HH:MM.
    const time = timePart ? timePart.slice(0, 5) : '';
    return { date, time };
}

/**
 * Join a date + time back into "YYYY-MM-DDTHH:MM".
 * Returns "" if neither part is set. If only one part is present, returns the
 * partial value so validation that checks for a complete value still fails
 * (mirrors a half-filled native datetime-local input).
 */
export function joinDateTimeLocal(date, time) {
    if (!date && !time) return '';
    return `${date || ''}T${time || ''}`;
}

/**
 * Convert a UTC ISO instant (e.g. an ICS-imported "2026-06-29T01:00:00.000Z")
 * into a "YYYY-MM-DDTHH:MM" datetime-local string for the form.
 *
 * Pass `zone` (an IANA name like "Australia/Brisbane") to render the wall-clock
 * in the EVENT'S ORG timezone — so a 01:00 UTC event pre-fills as 11:00 AM in
 * Brisbane regardless of where the organiser's browser is. Omit `zone` (or pass
 * null) to fall back to the browser's local zone.
 *
 * A naive `iso.slice(0, 16)` would keep the UTC wall-clock digits (1:00 AM),
 * which is wrong; this converts properly.
 *
 * Returns "" for empty/invalid input.
 */
export function isoToLocalDateTimeInput(isoString, zone = null) {
    if (!isoString) return '';
    const dt = zone
        ? DateTime.fromISO(isoString, { zone: 'utc' }).setZone(zone)
        : DateTime.fromISO(isoString); // browser-local
    if (!dt.isValid) return '';
    return dt.toFormat("yyyy-MM-dd'T'HH:mm");
}

/**
 * Convert a "YYYY-MM-DDTHH:MM" datetime-local string (the wall-clock the user
 * entered) into a UTC ISO string, interpreting it in the given IANA `zone`.
 *
 * This is the submit-boundary counterpart: the org enters "11:00 AM", we treat
 * that as 11:00 in the org's timezone and store the correct UTC instant. Omit
 * `zone` to fall back to browser-local interpretation (the legacy behaviour,
 * equivalent to `new Date(localString).toISOString()`).
 *
 * Returns the input unchanged if it's empty or already a UTC ISO (contains "Z").
 */
export function localInputToUTCISO(localString, zone = null) {
    if (!localString || localString.includes('Z')) return localString;
    const dt = zone
        ? DateTime.fromISO(localString, { zone })
        : DateTime.fromISO(localString); // browser-local
    if (!dt.isValid) return localString;
    return dt.toUTC().toISO();
}
